/**
 * "Share with team" — a private board moves into a team (30.09.2026).
 *
 * The SERVER moves it, in one transaction with a fixed number of statements
 * whatever the board's size (POST /sync/team/:tid/boards/:bid/move, backend
 * Sync.BoardMove). One request, instead of re-pushing every record into the
 * team and tombstoning every personal one (~2N writes). The client only makes
 * sure the server has the latest edits first, then follows:
 *
 *   desktop  1. a device-only board goes to the cloud first (records_move_to_cloud)
 *            2. unsent edits for the board are pushed (engine cycle), waited for
 *            3. the server move
 *            4. records_move_to_team_local: the same local rows change scope,
 *               personal → team, keeping their ids, queueing nothing
 *   web      1. queued writes flushed   2. the server move   3. one pull
 *
 * The old path wrote a team copy with the SAME ids while the personal rows
 * still existed; the desktop upsert refuses that (`scope_mismatch`, #202) and
 * the error was swallowed — the share did nothing.
 *
 * "Make private" is the mirror (01.10.2026, card 9c038d97): POST
 * …/boards/:bid/move-private (backend Sync.BoardMovePrivate), then
 * records_move_to_personal_local re-scopes team:<tid> → personal:<uid>. The
 * old moveBoardToPrivate wrote a personal copy with the same ids — the same
 * desktop `scope_mismatch`, swallowed.
 *
 * Like itemMove.js this is a data-residency change, so it is imported by UI
 * components only — never from the MCP graph (mcpAttackSurface.test.js).
 */
import { invoke } from '@tauri-apps/api/core';
import { isWebApp } from '../platform/target';
import { apiClient } from '../utils/apiClient';
import { useAuthStore } from '../store/authStore';
import { notePlanLimitRefusal } from '../store/planLimitStore';
import { installWebRecords } from './web/installWebRecords';

const BOARDS_EXTENSION_ID = 'task-manager';
const FLUSH_TIMEOUT_MS = 15000;
const FLUSH_POLL_MS = 250;

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

function currentUserId() {
  const { isAuthenticated, user } = useAuthStore.getState();
  if (!isAuthenticated || user?.id == null) throw new Error('signed_out');
  return String(user.id);
}

/** The server half: one request. Resolves `{ moved }` or `{ already_moved }`. */
export async function requestBoardMove(boardId, teamId) {
  const res = await apiClient.post(
    `/sync/team/${encodeURIComponent(teamId)}/boards/${encodeURIComponent(boardId)}/move`
  );
  return res.data;
}

/** "Make private": the server half. Resolves `{ moved }` or `{ already_moved }`. */
export async function requestBoardPrivate(boardId, teamId) {
  const res = await apiClient.post(
    `/sync/team/${encodeURIComponent(teamId)}/boards/${encodeURIComponent(boardId)}/move-private`
  );
  return res.data;
}

// Push the board's unsent edits and wait until none are left, so the server
// moves what the user sees. `teamId`: the board lives in that team's scope.
async function flushBoardEdits(userId, boardId, { timeoutMs = FLUSH_TIMEOUT_MS, pollMs = FLUSH_POLL_MS, teamId = null } = {}) {
  const args = { userId, extensionId: BOARDS_EXTENSION_ID, rootId: String(boardId) };
  const command = teamId ? 'records_team_item_pending_ops' : 'records_item_pending_ops';
  if (teamId) args.teamId = String(teamId);
  const scope = teamId ? `team:${teamId}` : `personal:${userId}`;
  const deadline = Date.now() + timeoutMs;
  for (;;) {
    const { pending } = await invoke(command, args); // eslint-disable-line no-await-in-loop
    if (!pending) return;
    if (Date.now() > deadline) throw new Error('pending_changes');
    await invoke('sync_pull_now', { workspaceId: scope }).catch(() => {}); // eslint-disable-line no-await-in-loop
    await sleep(pollMs); // eslint-disable-line no-await-in-loop
  }
}

async function moveOnDesktop(boardId, teamId, { onDevice, flush }) {
  const userId = currentUserId();
  const rootId = String(boardId);
  if (onDevice) {
    const up = await invoke('records_move_to_cloud', { userId, extensionId: BOARDS_EXTENSION_ID, rootId });
    if (up?.refused) {
      notePlanLimitRefusal(up.refused);
      throw new Error(up.refused);
    }
  }
  await flushBoardEdits(userId, rootId, flush);
  const result = await requestBoardMove(rootId, teamId);
  await invoke('records_move_to_team_local', { userId, extensionId: BOARDS_EXTENSION_ID, rootId, teamId: String(teamId) });
  // The team rows (with the new visibility) and the personal tombstones.
  await invoke('sync_pull_now', { workspaceId: `team:${teamId}` }).catch(() => {});
  await invoke('sync_pull_now', { workspaceId: `personal:${userId}` }).catch(() => {});
  return result;
}

async function moveOnWeb(boardId, teamId, request = requestBoardMove) {
  const backend = installWebRecords();
  await backend?.idle(); // queued writes reach the server first
  const result = await request(String(boardId), teamId);
  backend?.refresh(); // one pull per scope: tombstones + the team rows
  await backend?.idle();
  return result;
}

/**
 * Share a private board with a team. `onDevice`: the board is kept on this
 * device only (it goes to the cloud first). Rejects with the reason; nothing
 * moved anywhere when it rejects before the server answered.
 */
export async function shareBoardWithTeam(boardId, teamId, { onDevice = false, flush } = {}) {
  // The build target decides, exactly as installWebRecords does: the web app
  // holds its records in the web backend, whatever else the page can reach.
  if (isWebApp()) return moveOnWeb(boardId, teamId);
  return moveOnDesktop(boardId, teamId, { onDevice, flush });
}

/**
 * "Make private": a team board moves back to the current user's private
 * boards. Rejects with the reason (403: not a team admin and not the one who
 * shared it); nothing moved when it rejects before the server answered.
 */
export async function makeBoardPrivate(boardId, teamId, { flush } = {}) {
  if (isWebApp()) return moveOnWeb(boardId, teamId, requestBoardPrivate);
  const userId = currentUserId();
  const rootId = String(boardId);
  await flushBoardEdits(userId, rootId, { ...flush, teamId });
  const result = await requestBoardPrivate(rootId, teamId);
  await invoke('records_move_to_personal_local', { userId, extensionId: BOARDS_EXTENSION_ID, rootId, teamId: String(teamId) });
  // The private rows (with the new visibility) and the team tombstones.
  await invoke('sync_pull_now', { workspaceId: `personal:${userId}` }).catch(() => {});
  await invoke('sync_pull_now', { workspaceId: `team:${teamId}` }).catch(() => {});
  return result;
}
